import { foundationColorTokens } from './tokens/colors';
import { foundationMotionTokens } from './tokens/motion';
import { foundationRadiusTokens } from './tokens/radius';
import { foundationShadowTokens } from './tokens/shadow';
import { foundationSpacingTokens } from './tokens/spacing';
import { foundationTypographyTokens } from './tokens/typography';

export const foundationSemantic = {
  text: {
    primary: foundationColorTokens.text.primary,
    secondary: foundationColorTokens.text.secondary,
    muted: foundationColorTokens.text.muted,
    inverse: foundationColorTokens.text.inverse
  },
  interactive: {
    hoverSurface: 'hover:bg-surface-muted',
    hoverText: 'hover:text-ink-primary',
    selectedSurface: 'bg-brand/10',
    focusRing: 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
    disabled: 'disabled:cursor-not-allowed disabled:opacity-50'
  },
  input: {
    base: [
      foundationRadiusTokens.md,
      foundationShadowTokens.sm,
      foundationMotionTokens.colors,
      'file:border-0 file:bg-transparent file:text-sm file:font-medium',
      'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand',
      foundationSemanticPlaceholderDisabled()
    ].join(' '),
    field: `flex h-9 w-full ${foundationSpacingTokens.input.field}`,
    textarea: `w-full ${foundationSpacingTokens.input.textarea}`,
    surface: foundationColorTokens.background.surface,
    border: foundationColorTokens.border.subtle,
    text: `${foundationColorTokens.text.primary} ${foundationTypographyTokens.body.sm}`,
    placeholder: 'placeholder-ink-muted',
    invalid: 'border-red-500 focus-visible:ring-red-500'
  },
  action: {
    primary: `${foundationColorTokens.brand.surface} ${foundationColorTokens.brand.surfaceHover} ${foundationColorTokens.brand.foreground}`,
    secondary: `${foundationColorTokens.background.surface} ${foundationColorTokens.text.secondary} border ${foundationColorTokens.border.subtle} hover:bg-surface-muted`,
    danger: 'bg-red-600 hover:bg-red-700 text-white',
    ghost: `${foundationColorTokens.text.secondary} hover:bg-surface-muted`,
    icon: `${foundationColorTokens.text.secondary} hover:bg-surface-muted`
  },
  surface: {
    default: `${foundationColorTokens.background.surface} border ${foundationColorTokens.border.subtle}`,
    muted: `${foundationColorTokens.background.surfaceMuted} border ${foundationColorTokens.border.subtle}`,
    elevated: `${foundationColorTokens.background.surface} border ${foundationColorTokens.border.subtle} ${foundationShadowTokens.card}`,
    interactive: [
      foundationColorTokens.background.surface,
      'border',
      foundationColorTokens.border.subtle,
      foundationMotionTokens.emphasis,
      foundationMotionTokens.lift,
      foundationShadowTokens.cardHover,
      'hover:border-brand/50'
    ].join(' ')
  },
  field: {
    label: `block ${foundationTypographyTokens.label.default} ${foundationColorTokens.text.primary}`,
    description: `${foundationTypographyTokens.body.xs} ${foundationColorTokens.text.secondary}`,
    error: `${foundationTypographyTokens.body.xs} text-red-500`
  },
  menu: {
    container: `${foundationColorTokens.background.surface} border ${foundationColorTokens.border.subtle} ${foundationShadowTokens.floating}`,
    itemDefault: foundationColorTokens.text.secondary,
    itemDestructive: 'text-red-600 dark:text-red-400',
    itemSelected: 'bg-brand/10'
  },
  dialog: {
    overlay: foundationColorTokens.overlay.scrim,
    panel: `${foundationColorTokens.background.surface} border ${foundationColorTokens.border.subtle}`,
    header: `border-b ${foundationColorTokens.border.subtle}`,
    footer: `border-t ${foundationColorTokens.border.subtle}`,
    title: `${foundationTypographyTokens.heading.sm} ${foundationColorTokens.text.primary}`
  },
  nav: {
    itemBase: `${foundationColorTokens.text.secondary} hover:bg-surface-muted hover:text-ink-primary`,
    itemActive: `${foundationColorTokens.brand.surface} ${foundationColorTokens.brand.foreground}`
  },
  badge: {
    default: 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200',
    success: `${foundationColorTokens.status.successSurface} ${foundationColorTokens.status.successText}`,
    warning: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
    danger: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
    info: 'bg-brand/10 text-brand'
  },
  chip: {
    neutral: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
    selected: 'bg-brand/10 text-brand',
    interactive: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-surface-muted',
    dismissible: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 pr-1'
  },
  avatar: {
    fallbackPalette: [
      'bg-blue-600',
      'bg-emerald-600',
      'bg-rose-600',
      'bg-amber-600',
      'bg-indigo-600'
    ],
    text: 'text-white'
  }
} as const;

function foundationSemanticPlaceholderDisabled() {
  return 'disabled:cursor-not-allowed disabled:opacity-50';
}